import type { TagsCollection } from '@racemap/sdk/schema/starters';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';

type URLPrim = string | number | boolean | DateTime;
export type URLVal = URLPrim | Array<URLPrim> | Immutable<TagsCollection> | null | undefined;

/** `raw` is a string URLSearchParams encodes alone; `string` is encoded twice. */
export type ParamType = 'string' | 'raw' | 'number' | 'boolean' | 'tags' | 'date';

const KEY_VALUE_DELIMITER = ':';
const VALUE_DELIMITER = '|';
const GROUP_DELIMITER = ';';

const enc = (s: string) => encodeURIComponent(s);
const dec = (s: string) => decodeURIComponent(s);

const isNilOrEmptyArray = (v: unknown) =>
  v == null || (Array.isArray(v) && v.length === 0) || (v instanceof Map && v.size === 0);

const encodeTags = (tags?: Immutable<TagsCollection>): string | undefined => {
  if (!tags || tags.size === 0) return undefined;

  const groups: Array<string> = [];
  for (const [tagKey, tagValues] of tags.entries()) {
    if (!tagValues || tagValues.length === 0) continue;
    const encodedValues = tagValues
      .map((tag) => tag?.value)
      .filter(Boolean)
      .map(enc);
    if (!encodedValues.length) continue;
    groups.push(`${enc(tagKey)}${KEY_VALUE_DELIMITER}${encodedValues.join(VALUE_DELIMITER)}`);
  }

  return groups.length ? groups.join(GROUP_DELIMITER) : undefined;
};

const decodeTags = (value: string | null): Immutable<TagsCollection> | undefined => {
  if (!value) return undefined;

  const map: TagsCollection = new Map();
  const groups = value.split(GROUP_DELIMITER).filter(Boolean);

  for (const group of groups) {
    const [rawKey, rawValues] = group.split(KEY_VALUE_DELIMITER, 2);
    if (!rawKey || !rawValues) continue;

    const key = dec(rawKey);
    const values = rawValues
      .split(VALUE_DELIMITER)
      .filter(Boolean)
      .map((entry) => ({ value: dec(entry) }));

    if (values.length) map.set(key, values);
  }

  return map.size ? map : undefined;
};

function serializeItem(paramType: ParamType, v: any): string | undefined {
  if (v == null) return undefined;
  switch (paramType) {
    case 'string':
      return enc(String(v));
    case 'raw':
      return String(v);
    case 'number': {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? String(n) : undefined;
    }
    case 'boolean':
      return v === true || String(v).toLowerCase() === 'true' ? '1' : '0';
    case 'date':
      return v instanceof DateTime ? (v.toISO() ?? undefined) : undefined;
    case 'tags':
      return encodeTags(v);
  }
}

function deserializeItem(paramType: ParamType, s: string): any {
  switch (paramType) {
    case 'string':
      return dec(s);
    case 'raw':
      return s;
    case 'number': {
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'boolean':
      return s === '1' || s.toLowerCase() === 'true';
    case 'date': {
      const date = DateTime.fromISO(s);
      return date.isValid ? date : undefined;
    }
    case 'tags':
      return decodeTags(s);
  }
}

/**
 * Converts a value (single or array) into a serialized query string representation.
 * Example: ['red','blue'] → "red,blue"
 */
function toParamString<T extends URLVal>(
  paramType: ParamType,
  isMultiple: boolean,
  value: T,
): string | undefined {
  if (isNilOrEmptyArray(value)) return undefined;
  if (isMultiple) {
    const arr = Array.isArray(value) ? value : [value];
    const parts = arr
      .map((item) => serializeItem(paramType, item))
      .filter((p): p is string => p !== undefined);
    return parts.length ? parts.join(',') : undefined;
  }
  return serializeItem(paramType, value);
}

/**
 * Parses a serialized query parameter value back into its native form.
 * Supports single and multiple values.
 */
export function fromParamString<T extends URLVal>(
  paramType: ParamType,
  isMultiple: boolean,
  raw: string | null,
  defaultValue?: T,
): T {
  if (raw == null || raw === '') {
    if (defaultValue != null) return defaultValue;
    return (isMultiple ? [] : undefined) as T;
  }
  if (isMultiple) {
    const parts = raw.split(',').filter((p) => p.length > 0);
    const vals = parts.map((p) => deserializeItem(paramType, p)).filter((x) => x !== undefined);
    return vals as T;
  }
  return deserializeItem(paramType, raw);
}

/**
 * Updates a URLSearchParams object with a given key/value pair.
 * Automatically removes the param if the value is empty or undefined.
 */
export function updateParam(
  params: URLSearchParams,
  key: string,
  value: URLVal,
  options: { paramType: ParamType; isMultiple: boolean },
): void {
  if (value == null || isEmptyString(value) || isNilOrEmptyArray(value)) {
    params.delete(key);
    return;
  }

  const serialized = toParamString(options.paramType, options.isMultiple, value);

  if (serialized) {
    params.set(key, serialized);
  } else {
    params.delete(key);
  }
}
